import { useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import type { ContactSuggestion, DirectConversation } from '../../../types';
import { useUpdateConversation } from '../../../hooks/use-update-conversation';
import { ChatBoxSettingsMembersList } from './ChatBoxSettingsMembersList';
import { AddParticipantInput } from './AddParticipantInput';
import { ChatBoxSettingsButton } from './ChatBoxSettingsButton';

/**
 * The "Members" tab content of the chat box conversation settings.
 * @see {@link ChatBoxSettings}
 */
export function ChatBoxSettingsMembers({
    className,
    conversation,
    onClose,
}: {
    className?: string;
    conversation: DirectConversation;
    onClose?: () => void;
}) {
    const [isAddingParticipant, setIsAddingParticipant] = useState(false);
    const [participantsToAdd, setParticipantsToAdd] = useState<
        ContactSuggestion[]
    >([]);
    const { isSubmitting, addParticipants, removeParticipant } =
        useUpdateConversation({
            conversationID: Number(conversation.ID),
            onAddSuccess: () => {
                setParticipantsToAdd([]);
                onClose?.();
            },
            onRemoveSuccess: onClose,
        });

    return (
        <div className={cn('flex flex-col gap-[8px]', className)}>
            {!isAddingParticipant ? (
                <>
                    {conversation.IsGroupChat && (
                        <button
                            className="flex h-[44px] w-full items-center gap-[8px] rounded-full p-[6px] hover:bg-white/50 disabled:pointer-events-none disabled:opacity-50"
                            disabled={isSubmitting}
                            onClick={() => setIsAddingParticipant(true)}
                        >
                            <PlusIcon
                                className="size-[32px] shrink-0 rounded-full border border-black p-[4px]"
                                strokeWidth="1.5px"
                            />
                            <span className="min-w-0 flex-1 break-words text-left text-[14px] font-medium leading-tight">
                                Add people to this conversation
                            </span>
                        </button>
                    )}
                    <ChatBoxSettingsMembersList
                        conversation={conversation}
                        isDisabled={isSubmitting}
                        onRemoveParticipant={removeParticipant}
                    />
                </>
            ) : (
                <AddParticipantInput
                    className="min-h-0 flex-1"
                    inputClassName="h-[44px]"
                    participants={conversation.Participants}
                    participantsToAdd={participantsToAdd}
                    isDisabled={isSubmitting}
                    onParticipantsChange={setParticipantsToAdd}
                />
            )}
            <div className="mt-auto flex items-center justify-end gap-[4px]">
                {isAddingParticipant && (
                    <ChatBoxSettingsButton
                        className="hover:bg-white/50"
                        color="transparent"
                        disabled={isSubmitting}
                        onClick={() => {
                            setIsAddingParticipant(false);
                            setParticipantsToAdd([]);
                        }}
                    >
                        Back
                    </ChatBoxSettingsButton>
                )}
                <ChatBoxSettingsButton
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                    onClick={() => {
                        if (isAddingParticipant) {
                            addParticipants(participantsToAdd.map((p) => p.ID));
                        } else {
                            onClose?.();
                        }
                    }}
                >
                    Save & Close
                </ChatBoxSettingsButton>
            </div>
        </div>
    );
}
